import { EventTypeEnum, ThemeEntity } from "@citychallenge/api-public";
import { escapeRegex, debounce } from "@citychallenge/common";
import { observable, action, computed } from "mobx";
import { Translation } from "../i18n";
import { EventModel, EventParticipantModel } from "./event.model";
import dayjs from "dayjs";

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

/**
 * A session stays joinable for an hour after it starts, so late participants
 * can still get in. Stakeholder-owned business rule — see
 * event-webinar-subscribe-selector, which disables its date options by it.
 */
export const WEBINAR_JOIN_GRACE_MS = 60 * 60 * 1000;

/** What the webinar list counts as still ahead: over two hours out. */
function isUpcomingWebinar(event: EventModel) {
  return Date.parse(event.highestStartDate) > Date.now() + TWO_HOURS_MS;
}

export class TranslatableTheme {
  public id!: string;
  public name!: string;

  public translation?: Translation;

  @observable
  public active = false;

  @action
  public toggleActive = () => {
    this.active = !this.active;
  };

  constructor(data: { id: string; name: string; translation?: Translation }) {
    Object.assign(this, data);
  }
}

export class EventStore {
  constructor(events: EventModel[] = []) {
    this.events = events;
  }

  @observable
  public participations: EventParticipantModel[] = [];

  @action
  public setParticipations(participations: EventParticipantModel[]) {
    this.participations = participations;
  }

  @observable
  public events: EventModel[];

  @action
  public addEvent(event: EventModel) {
    this.events.push(event);

    this.events = this.events.slice().sort((a, b) => {
      return a.startDate > b.startDate ? 1 : -1;
    });
  }

  @action
  public setEvents(events: EventModel[]) {
    this.events = events;
  }

  public findById(articleId: string) {
    return this.events.find(({ id }) => {
      return id === articleId;
    });
  }

  @observable
  public searchFilter = "";

  @action
  public setSearchFilter = (value: string) => {
    this.searchFilter = value;
    this.resetThemes();
  };

  public setSearchFilterDebounced = debounce(this.setSearchFilter, 300);

  public resetSearchFilter = () => {
    this.setSearchFilter("");
  };

  @computed
  public get webinars() {
    return this.events.filter(
      (event) => !event.eventType || event.eventType === EventTypeEnum.Webinar
    );
  }

  @computed
  public get regularEvents() {
    return this.events.filter(
      (event) => event.eventType === EventTypeEnum.Event
    );
  }

  @computed
  public get filteredWebinars() {
    const { activeThemes, webinars } = this;

    if (this.searchFilter.length) {
      const regex = new RegExp(escapeRegex(this.searchFilter), "i");
      return webinars.filter((event) => event.contains(regex));
    }

    if (activeThemes.length) {
      return webinars.filter((event) => {
        for (const { id } of activeThemes) {
          if (event.theme?.id === id) {
            return true;
          }
        }
        return false;
      });
    }

    return webinars;
  }
  @computed
  public get futureFilteredWebinars() {
    return this.filteredWebinars.filter(isUpcomingWebinar);
  }

  @computed
  public get pastFilteredWebinars() {
    return this.filteredWebinars
      .filter((event) => !isUpcomingWebinar(event))
      .sort(
        (a, b) =>
          Date.parse(a.highestStartDate) - Date.parse(b.highestStartDate)
      );
  }

  @computed
  public get filteredWebinarsOrdered() {
    return [...this.futureFilteredWebinars, ...this.pastFilteredWebinars];
  }

  @computed
  public get pastRegularEvents() {
    return this.regularEvents
      .filter((event) => event.inPast)
      .sort((a, b) => Date.parse(b.startDate) - Date.parse(a.startDate));
  }

  @computed
  public get futureRegularEvents() {
    return this.regularEvents
      .filter((event) => !event.inPast)
      .sort((a, b) => Date.parse(a.startDate) - Date.parse(b.startDate));
  }

  @computed
  public get themes(): TranslatableTheme[] {
    const allThemes: { [key: string]: ThemeEntity } = {};

    for (const { theme } of this.webinars) {
      if (!theme) continue;

      if (!theme.id || theme.id in allThemes) continue;
      allThemes[theme.id] = theme;
    }

    return Object.values(allThemes).map((theme, index) => {
      return new TranslatableTheme({
        id: theme.id || `${index}`,
        name: theme.name,
      });
    });
  }

  @computed
  public get upcomingParticipatingEvents() {
    return this.events
      .sort((a, b) => +dayjs(a.startDate).unix() - +dayjs(b.startDate).unix())
      .filter((event) => {
        return event.isParticipating && event.eventState !== "in-past";
      });
  }

  public get firstJoinableEvent() {
    return this.events.find((event) => {
      return (
        event.isParticipating &&
        (event.eventState === "starts-soon" ||
          event.eventState === "in-progress")
      );
    });
  }

  @computed
  public get activeThemes() {
    return this.themes.filter((t) => t.active);
  }

  @action
  public resetThemes = () => {
    for (const theme of this.themes) {
      theme.active = false;
    }
  };
}
